/**
 * variant-media-filter.js
 *
 * Filters the Dawn product media gallery down to the media belonging to the
 * currently selected variant option (Color by default). Written for Dawn 15.x.
 *
 * The basic install needs no Liquid edits -- the script reads the group key out
 * of each image's alt text and then strips the marker from the rendered alt, so
 * screen readers and JS-rendering crawlers see clean, descriptive alt text.
 *
 * Two safeguards worth knowing about:
 *   - Media with no group marker stays visible for every variant. Use this for
 *     size charts, scale shots, videos and 3D models.
 *   - If the selected option matches no media at all, everything is shown
 *     rather than leaving the customer with an empty gallery.
 */

(function () {
  'use strict';

  var CONFIG = {
    /* The option to filter on, named exactly as it is in the product admin. */
    optionName: 'Color',

    /* Marker inside alt text: "Puffer jacket, front view ##Black" */
    delimiter: '##',

    /* Strip the marker from the alt attribute once it has been read. */
    cleanAlt: true,

    /* Filter immediately on page load using the preselected variant. */
    filterOnLoad: true
  };

  var GROUP_ATTR = 'data-media-group';
  var HIDDEN_ATTR = 'data-media-hidden';
  var STYLE_ID = 'variant-media-filter-styles';

  var running = false;
  var frame = null;
  var timers = [];

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function normalize(value) {
    return (value || '').trim().toLowerCase();
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.product__media-item[' + HIDDEN_ATTR + '],' +
      '.thumbnail-list__item[' + HIDDEN_ATTR + '],' +
      '.product-media-modal__content > [' + HIDDEN_ATTR + ']' +
      '{display:none !important;}';
    document.head.appendChild(style);
  }

  /* ------------------------------------------------------------------ */
  /* Reading the variant picker                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Returns every value offered for the configured option, plus the one that
   * is currently selected. Handles Dawn's swatch, pill and dropdown pickers.
   */
  function readPicker(root) {
    var target = normalize(CONFIG.optionName);
    var values = new Set();
    var selected = '';

    root.querySelectorAll('.product-form__input').forEach(function (group) {
      var labelEl = group.querySelector('legend, .form__label');
      if (!labelEl) return;

      /* Dawn renders swatch and pill legends as "Color: Black". */
      var name = normalize(labelEl.textContent.split(':')[0]);
      if (name !== target) return;

      var select = group.querySelector('select');
      if (select) {
        Array.prototype.forEach.call(select.options, function (option) {
          values.add(normalize(option.value));
        });
        selected = normalize(select.value);
        return;
      }

      group.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        values.add(normalize(radio.value));
        if (radio.checked) selected = normalize(radio.value);
      });
    });

    return { values: values, selected: selected };
  }

  /* ------------------------------------------------------------------ */
  /* Reading a media item's group                                        */
  /* ------------------------------------------------------------------ */

  function readGroup(item, knownValues) {
    /* Server-rendered value wins -- see INSTALL.md step 5. */
    if (item.hasAttribute(GROUP_ATTR)) {
      return normalize(item.getAttribute(GROUP_ATTR));
    }

    var img = item.querySelector('img[alt]');
    if (!img) {
      /* Videos and 3D models have no alt: treat them as shared media. */
      item.setAttribute(GROUP_ATTR, '');
      return '';
    }

    var alt = img.getAttribute('alt') || '';
    var index = alt.indexOf(CONFIG.delimiter);

    if (index === -1) {
      /* Also supports the plain convention where alt text is just "Black". */
      var bare = normalize(alt);
      var plain = knownValues.has(bare) ? bare : '';
      item.setAttribute(GROUP_ATTR, plain);
      return plain;
    }

    var group = normalize(alt.slice(index + CONFIG.delimiter.length));
    item.setAttribute(GROUP_ATTR, group);
    if (CONFIG.cleanAlt) img.setAttribute('alt', alt.slice(0, index).trim());
    return group;
  }

  /* ------------------------------------------------------------------ */
  /* Applying the filter                                                 */
  /* ------------------------------------------------------------------ */

  function syncThumbnails(root, hiddenIds) {
    root.querySelectorAll('.thumbnail-list__item').forEach(function (thumb) {
      var trigger = thumb.hasAttribute('data-target')
        ? thumb
        : thumb.querySelector('[data-target]');
      var target = trigger ? trigger.getAttribute('data-target') : null;

      if (target && hiddenIds.has(target)) thumb.setAttribute(HIDDEN_ATTR, '');
      else thumb.removeAttribute(HIDDEN_ATTR);
    });
  }

  function syncModal(root, hiddenIds) {
    var modal = root.querySelector('.product-media-modal__content');
    if (!modal) return;

    Array.prototype.forEach.call(modal.children, function (child) {
      var id = child.getAttribute('data-media-id');
      if (id && hiddenIds.has(id)) child.setAttribute(HIDDEN_ATTR, '');
      else child.removeAttribute(HIDDEN_ATTR);
    });
  }

  /**
   * Dawn's slider counts pages from items with a non-zero width, so hidden
   * slides drop out of the count as soon as the pages are recalculated.
   */
  function refreshSliders(gallery) {
    gallery.querySelectorAll('slider-component').forEach(function (slider) {
      if (typeof slider.initPages !== 'function') return;
      try {
        slider.initPages();
      } catch (error) {
        /* A slider that refuses to recount is not worth breaking the page over. */
      }
    });
  }

  function ensureActive(gallery, items, firstVisible) {
    if (!firstVisible) return;

    var activeIsVisible = items.some(function (item) {
      return item.classList.contains('is-active') && !item.hasAttribute(HIDDEN_ATTR);
    });
    if (activeIsVisible) return;

    var id = firstVisible.getAttribute('data-media-id');
    if (!id || typeof gallery.setActiveMedia !== 'function') return;

    try {
      /* prepend: false -- reordering the slides here fights Dawn's own logic. */
      gallery.setActiveMedia(id, false);
    } catch (error) {
      /* Non-fatal: the correct media is visible either way. */
    }
  }

  function applyFilter(gallery) {
    var root = gallery.closest('.shopify-section') || gallery.closest('.product') || document;
    var picker = readPicker(root);

    var items = Array.prototype.slice.call(gallery.querySelectorAll('.product__media-item'));
    if (!items.length) return;

    var groups = items.map(function (item) {
      return readGroup(item, picker.values);
    });

    var hasMatch = picker.selected !== '' && groups.indexOf(picker.selected) !== -1;

    var hiddenIds = new Set();
    var firstVisible = null;

    items.forEach(function (item, i) {
      var show = !hasMatch || groups[i] === '' || groups[i] === picker.selected;

      if (show) {
        item.removeAttribute(HIDDEN_ATTR);
        if (!firstVisible) firstVisible = item;
        return;
      }

      item.setAttribute(HIDDEN_ATTR, '');
      var id = item.getAttribute('data-media-id');
      if (id) hiddenIds.add(id);
    });

    syncThumbnails(root, hiddenIds);
    syncModal(root, hiddenIds);
    refreshSliders(gallery);
    ensureActive(gallery, items, firstVisible);
  }

  /* ------------------------------------------------------------------ */
  /* Scheduling                                                          */
  /* ------------------------------------------------------------------ */

  function run() {
    running = true;
    document.querySelectorAll('media-gallery').forEach(applyFilter);
    running = false;
  }

  /**
   * Dawn refetches and replaces product markup after a variant change, so one
   * pass on the change event is not enough -- the later passes catch the
   * re-rendered gallery.
   */
  function schedule() {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(run);

    while (timers.length) clearTimeout(timers.pop());
    timers.push(setTimeout(run, 250));
    timers.push(setTimeout(run, 700));
  }

  function observe() {
    var observer = new MutationObserver(function (mutations) {
      if (running) return;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          schedule();
          return;
        }
      }
    });

    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      var parent = gallery.parentElement;
      if (parent) observer.observe(parent, { childList: true, subtree: true });
    });
  }

  function init() {
    injectStyles();
    if (CONFIG.filterOnLoad) schedule();
    observe();
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (target.closest('.product-form__input, variant-selects, variant-radios')) {
      schedule();
    }
  });

  /* Theme editor reloads sections without a page refresh. */
  document.addEventListener('shopify:section:load', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();